import { memo, useMemo } from "react";
import { CChart } from "@coreui/react-chartjs";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const CHART_HEIGHT = 220;

function LineChart({ data = [], labels }) {
  const chartLabels = useMemo(() => {
    if (Array.isArray(labels) && labels.length > 0) return labels;
    return MONTHS;
  }, [labels]);

  const isDailyChart = chartLabels.length > 12;

  const values = useMemo(
    () => (Array.isArray(data) ? data.map((v) => Number(v) || 0) : []),
    [data]
  );

  const yMax = useMemo(() => {
    const maxVal = values.length ? Math.max(...values, 0) : 0;
    return maxVal <= 0 ? 8 : Math.max(Math.ceil(maxVal * 1.3), maxVal + 1);
  }, [values]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      resizeDelay: 200,
      interaction: { mode: "index", intersect: false, axis: "x" },
      layout: {
        padding: { top: 20, right: 12, bottom: 6, left: 4 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "#0f172a",
          titleColor: "#94a3b8",
          bodyColor: "#ffffff",
          borderColor: "transparent",
          borderWidth: 0,
          displayColors: false,
          padding: { top: 8, bottom: 8, left: 12, right: 12 },
          cornerRadius: 8,
          caretSize: 6,
          caretPadding: 8,
          titleFont: { size: 11, weight: "700" },
          bodyFont: { size: 13, weight: "700" },
          callbacks: {
            title: (items) => {
              const label = items[0]?.label ?? "";
              return isDailyChart && label ? `Day ${label}` : label;
            },
            label: (ctx) =>
              `${ctx.parsed.y} new driver${ctx.parsed.y === 1 ? "" : "s"}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#94a3b8",
            font: { size: 11, weight: "500" },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: isDailyChart ? 15 : 12,
          },
          border: { display: false },
        },
        y: {
          beginAtZero: true,
          max: yMax,
          ticks: {
            stepSize: yMax <= 8 ? 2 : undefined,
            color: "#94a3b8",
            font: { size: 11, weight: "500" },
            padding: 6,
            precision: 0,
          },
          grid: {
            color: "rgba(226, 232, 240, 0.85)",
            drawBorder: false,
          },
          border: { display: false },
        },
      },
      elements: {
        line: { tension: 0.35, borderWidth: 2.5 },
        point: {
          radius: isDailyChart ? 3 : 4,
          hoverRadius: 7,
          hitRadius: 22,
          hoverBorderWidth: 2,
        },
      },
    }),
    [yMax, isDailyChart]
  );

  const chartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          label: "New Drivers",
          data: values,
          fill: true,
          backgroundColor: "rgba(251, 152, 9, 0.14)",
          borderColor: "#fb9809",
          pointBackgroundColor: values.map((v) =>
            v > 0 ? "#fb9809" : "rgba(251, 152, 9, 0.35)"
          ),
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          pointHoverBackgroundColor: "#ea580c",
          pointHoverBorderColor: "#ffffff",
        },
      ],
    }),
    [values, chartLabels]
  );

  return (
    <div className="dash-driver-chart__plot" aria-hidden="false">
      <CChart
        type="line"
        height={CHART_HEIGHT}
        customTooltips={false}
        options={options}
        data={chartData}
      />
    </div>
  );
}

export default memo(LineChart);
